import { ILeadNote, ILeadNoteWithIndex } from 'common/interfaces/provider';
import { Search } from 'components/action';
import { SectionHeadline } from 'components/content';
import * as lunr from 'lunr';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';
import * as zenscroll from 'zenscroll';
import isNil from 'lodash-es/isNil';
import { LeadNoteNew } from './Note';
import moment = require('moment');
import { LeadNotesGroup } from './LeadNotesGroup';

// Types
export interface ILeadNotesGroup {
  date: string;
  notes: ILeadNote[];
}

export interface ILeadNotesGroupWithIndex extends ILeadNotesGroup {
  notes: ILeadNoteWithIndex[];
}

// Styles
const s = require('./LeadNotes.css');
const searchIcon = require('./../../../../assets/loupe icon blue.svg');
const addIcon = require('./../../../../assets/plus icon.svg');

// Component
@inject('store')
@observer
export class LeadNotes extends React.Component<IAppOnlyStore, {}> {
  private notesTop: HTMLDivElement | null = null;

  private windowScroller = zenscroll.createScroller(
    (document.scrollingElement || document.body) as any,
    300,
    128
  );

  @observable
  private searchQuery: string = '';
  @observable private isOnAddNoteScreen: boolean = true;

  @computed
  private get searchNotesIndex(): lunr.Index {
    const allLeadNotes = this.props.store.lead.openLeadNotes;

    return lunr(function () {
      this._b = 0;
      this._k1 = 0;

      this.field('text');
      this.field('author');

      allLeadNotes.map((note) => {
        this.add({
          id: note.id,
          text: note.comment,
          author: note.createdByUser.name || note.createdByUser.username,
        });
      });
    });
  }

  @computed
  private get filteredNotes(): ILeadNote[] {
    return this.props.store.lead.openLeadNotes.filter((note) => note.comment);
  }

  @computed
  private get searchNotes(): ILeadNote[] {
    const query = this.searchQuery.trim();
    if (query === '') {
      return this.filteredNotes;
    }

    const notesIds = this.filteredNotes.map((note) => note.id);
    const words = query.split(/\s/gm);
    const search = this.searchNotesIndex.search(
      words.map((word) => `${word}~1 ${word}*`).join(' ')
    );
    if (search.length <= 0) {
      return [];
    }

    const searchMatchingIds = search.map((result) => parseInt(result.ref, 10));
    const results = searchMatchingIds.map((id) => {
      const index = notesIds.indexOf(id);
      return this.filteredNotes[index];
    });

    return results;
  }

  @computed
  private get groupedSearchNotes(): ILeadNotesGroup[] {
    let previousDate = moment('1901-01-01');

    return this.searchNotes.reduce((noteGroups: ILeadNotesGroup[], note) => {
      let noteDate: moment.Moment | null = null;
      if (!isNil(note.updatedAt)) {
        const updatedAtParsed = moment(note.updatedAt);
        noteDate = updatedAtParsed.isValid() ? updatedAtParsed : null;
      } else if (!isNil(note.createdAt)) {
        const createdAtParsed = moment(note.createdAt);
        noteDate = createdAtParsed.isValid() ? createdAtParsed : null;
      }

      if (noteDate.isSame(previousDate, 'day')) {
        noteGroups[noteGroups.length - 1].notes.push(note);
      } else {
        noteGroups.push({
          date: noteDate.format('YYYY-MM-DD'),
          notes: [note],
        });

        previousDate = noteDate;
      }

      return noteGroups;
    }, []);
  }

  @computed
  private get groupedSearchNotesWithIndex(): ILeadNotesGroupWithIndex[] {
    let index = -1;

    return this.groupedSearchNotes.map((group) => ({
      ...group,
      notes: group.notes.map((note) => {
        index++;

        return {
          ...note,
          index,
        };
      }),
    }));
  }

  public render() {
    const { lead } = this.props.store;

    return (
      <div className={s.container}>
        <SectionHeadline
          title="Notes"
          button={{
            onClick: this.toggleAddNoteScreen,
            iconClassName: this.isOnAddNoteScreen ? s.searchIcon : s.addIcon,
            icon: this.isOnAddNoteScreen ? searchIcon : addIcon,
            label: this.isOnAddNoteScreen ? 'Search notes' : 'Add new note',
          }}
        />

        <div className={s.group}>
          <div className={s.notes} ref={this.referenceNotesTop}>
            {(this.isOnAddNoteScreen && <LeadNoteNew />) || (
              <Search
                onValueChange={this.changeSearchValue}
                className={s.notesSearchContainer}
                classNameForField={s.notesSearchField}
                label="Search lead's notes"
                placeholder="Note's content"
                value={this.searchQuery}
                isValueClearable={true}
                autoFocus={true}
              />
            )}
          </div>

          {lead.openLeadDetails !== null &&
            this.groupedSearchNotesWithIndex.length > 0 && (
              <div className={s.notes}>
                {this.groupedSearchNotesWithIndex.map((notesGroup) => {
                  return (
                    <LeadNotesGroup key={notesGroup.date} group={notesGroup} />
                  );
                })}
              </div>
            )}

          {lead.openLeadDetails !== null &&
            this.groupedSearchNotesWithIndex.length <= 0 && (
              <p className={s.noNotes}>
                {this.searchQuery
                  ? 'No notes match search query.'
                  : 'No notes exist for this lead.'}
              </p>
            )}
        </div>
      </div>
    );
  }

  private referenceNotesTop = (ref: HTMLDivElement) => (this.notesTop = ref);

  @action
  private toggleAddNoteScreen = () => {
    this.isOnAddNoteScreen = !this.isOnAddNoteScreen;
    this.searchQuery = '';
  };

  @action
  private changeSearchValue = (value: string) => {
    this.searchQuery = value;

    if (this.notesTop) {
      this.windowScroller.intoView(this.notesTop);
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Notes/LeadNotes.tsx